import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  DragIndicator,
  Email,
  Phone,
  Event,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';

interface BoardMember {
  id: string;
  club_id: string;
  title: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  term_expires: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
}

interface BoardMembersManagerProps {
  clubId: string;
}

const BoardMembersManager: React.FC<BoardMembersManagerProps> = ({ clubId }) => {
  const { t, i18n } = useTranslation();
  const [members, setMembers] = useState<BoardMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<BoardMember | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<BoardMember | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    term_expires: null as Date | null,
    display_order: 0,
  });

  useEffect(() => {
    if (clubId) {
      fetchMembers();
    }
  }, [clubId]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/clubs/${clubId}/board-members`);
      setMembers(response.data.items || []);
    } catch (err: any) {
      console.error('Failed to fetch board members:', err);
      setError(t('boardMembers.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (member?: BoardMember) => {
    if (member) {
      setEditingMember(member);
      setFormData({
        title: member.title,
        first_name: member.first_name,
        last_name: member.last_name,
        email: member.email || '',
        phone: member.phone || '',
        term_expires: member.term_expires ? new Date(member.term_expires) : null,
        display_order: member.display_order,
      });
    } else {
      setEditingMember(null);
      setFormData({
        title: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        term_expires: null,
        display_order: members.length,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingMember(null);
    setFormData({
      title: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      term_expires: null,
      display_order: 0,
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const payload = {
        title: formData.title,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email || null,
        phone: formData.phone || null,
        term_expires: formData.term_expires ? formData.term_expires.toISOString() : null,
        display_order: formData.display_order,
      };

      if (editingMember) {
        await api.put(`/clubs/${clubId}/board-members/${editingMember.id}`, payload);
      } else {
        await api.post(`/clubs/${clubId}/board-members`, payload);
      }

      setSuccess(t('common.savedSuccessfully'));
      setTimeout(() => setSuccess(null), 3000);
      handleCloseDialog();
      fetchMembers();
    } catch (err: any) {
      console.error('Failed to save board member:', err);
      setError(t('boardMembers.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (member: BoardMember) => {
    setMemberToDelete(member);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!memberToDelete) return;

    try {
      setSaving(true);
      await api.delete(`/clubs/${clubId}/board-members/${memberToDelete.id}`);
      setSuccess(t('common.deletedSuccessfully'));
      setTimeout(() => setSuccess(null), 3000);
      setDeleteConfirmOpen(false);
      setMemberToDelete(null);
      fetchMembers();
    } catch (err: any) {
      console.error('Failed to delete board member:', err);
      setError(t('boardMembers.errors.deleteFailed'));
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString(i18n.language);
  };

  const isExpired = (dateString: string | null) => {
    if (!dateString) return false;
    return new Date(dateString) < new Date();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">{t('boardMembers.title')}</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            {t('boardMembers.addMember')}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {members.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {t('boardMembers.noMembers')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('boardMembers.noMembersDescription')}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
            >
              {t('boardMembers.addMember')}
            </Button>
          </Paper>
        ) : (
          <List>
            {members.map((member) => (
              <Paper key={member.id} sx={{ mb: 1 }}>
                <ListItem>
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                    <DragIndicator color="action" />
                  </Box>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {member.title}
                        </Typography>
                        <Typography variant="body1">
                          - {member.first_name} {member.last_name}
                        </Typography>
                        {member.term_expires && (
                          <Chip
                            icon={<Event />}
                            label={formatDate(member.term_expires)}
                            size="small"
                            color={isExpired(member.term_expires) ? 'error' : 'default'}
                            variant={isExpired(member.term_expires) ? 'filled' : 'outlined'}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                        {member.email && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Email fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {member.email}
                            </Typography>
                          </Box>
                        )}
                        {member.phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Phone fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {member.phone}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="edit"
                      onClick={() => handleOpenDialog(member)}
                      sx={{ mr: 1 }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleDeleteClick(member)}
                    >
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </Paper>
            ))}
          </List>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingMember ? t('boardMembers.editMember') : t('boardMembers.addMember')}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                fullWidth
                required
                label={t('boardMembers.memberTitle')}
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={t('boardMembers.titlePlaceholder')}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  required
                  label={t('boardMembers.firstName')}
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                />
                <TextField
                  fullWidth
                  required
                  label={t('boardMembers.lastName')}
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                />
              </Box>
              <TextField
                fullWidth
                type="email"
                label={t('boardMembers.email')}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
              <TextField
                fullWidth
                label={t('boardMembers.phone')}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
              <DatePicker
                label={t('boardMembers.termExpires')}
                value={formData.term_expires}
                onChange={(date) => setFormData({ ...formData, term_expires: date as Date | null })}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: t('boardMembers.termExpiresHelper'),
                  },
                }}
              />
              <TextField
                fullWidth
                type="number"
                label={t('boardMembers.displayOrder')}
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 0 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>{t('boardMembers.cancel')}</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !formData.title || !formData.first_name || !formData.last_name}
            >
              {saving ? t('boardMembers.saving') : t('boardMembers.save')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
          <DialogTitle>{t('boardMembers.deleteConfirmTitle')}</DialogTitle>
          <DialogContent>
            <Typography>{t('boardMembers.deleteConfirmMessage')}</Typography>
            {memberToDelete && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {memberToDelete.title}: {memberToDelete.first_name} {memberToDelete.last_name}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>{t('boardMembers.cancel')}</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteConfirm}
              disabled={saving}
            >
              {t('boardMembers.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default BoardMembersManager;
